import { useMemo, useState, useEffect } from "react";
import { Button } from "@shared/ui/button";
import { cn } from "@shared/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { Checkbox } from "@shared/ui/checkbox";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormService, FormField } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { Download, Info } from "lucide-react";
import { useGetSoCounts, useGetGeneratedReport } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

export const exportSections = [
  {
    id: 1,
    title: "1. Service Order Overview",
    description: "Identity, type, and high-level classification.",
    fields: ["Service Order ID", "SO Name", "SO Status", "Priority", "Utility Type", "Operation Type", "Type", "Sub-type", "Request Type", "Source", "Service Association", "Template ID", "Template Version"]
  },
  {
    id: 2,
    title: "2. SLA, Planning & Safety",
    description: "Everything related to planning, SLA, and readiness.",
    fields: ["SLA Status", "SLA Rules", "Estimated Efforts", "Estimated Downtime", "Required Fieldforce", "Total Tasks", "Safety Package"]
  },
  {
    id: 3,
    title: "3. Territory & Location",
    description: "Where the service is executed.",
    fields: ["City", "Zone", "Division", "Area", "Sub-area", "Premise"]
  },
  {
    id: 4,
    title: "4. Assignment & Lifecycle",
    description: "Who created, who executed, and timeline events.",
    fields: ["Created On", "Created By", "Assign To", "Assigned On", "Task Completed"]
  },
  {
    id: 5,
    title: "5. Cost & Effort (Estimated vs Actual)",
    description: "Financial and effort comparison for ops + finance.",
    fields: ["Est Labour Cost", "Est Material Cost", "Tax Rate", "Actual Labour Cost", "Actual Material Cost", "Actual Other Cost", "Actual Total Cost", "Actual Efforts", "Actual Downtime", "Actual Technician Count"]
  },
  {
    id: 6,
    title: "6. Approval, Rework & Exceptions",
    description: "Governance and exception handling.",
    fields: ["Approved By", "Approved On", "Rejected By", "Rejected On", "Rework SO", "Rework Created By", "Rework On"]
  }
];

interface ExportMasterDataDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ExportMasterDataDialog = ({ isOpen, onClose }: ExportMasterDataDialogProps) => {
  const [selectedSections, setSelectedSections] = useState<number[]>([]);
  const remoteUtilityId = useRemoteUtilityId();
  const [dateParams, setDateParams] = useState<{ startDate: string; endDate: string; } | null>(null);
  const { data: countsData, isFetching: isFetchingCounts, refetch: refetchCounts } = useGetSoCounts(dateParams?.startDate, dateParams?.endDate);

  const handleSelectAll = () => {
    if (selectedSections.length === exportSections.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(exportSections.map((s) => s.id));
    }
  };

  const handleToggleSection = (id: number) => {
    setSelectedSections((prev) =>
      prev.includes(id) ? prev.filter((sId) => sId !== id) : [...prev, id]
    );
  };

  const exportFields: FormField[] = useMemo(
    () => [
      {
        name: "startDate",
        label: "Start Date",
        type: "datepicker",
        required: true,
        className: "col-span-1",
      },
      {
        name: "endDate",
        label: "End Date",
        type: "datepicker",
        className: "col-span-1",
        required: true,
      },
    ],
    []
  );

  const exportForm = FormService.createForm({ fields: exportFields });

  useEffect(() => {
    if (!isOpen) {
      exportForm.reset();
      setSelectedSections([]);
      setDateParams(null);
    }
  }, [isOpen, exportForm]);

  const sectionToGroupMap: Record<number, string> = useMemo(() => ({
    1: "SERVICE_ORDER_OVERVIEW",
    2: "SLA_PLANNING_SAFETY",
    3: "TERRITORY_LOCATION",
    4: "ASSIGNMENT_LIFECYCLE",
    5: "COST_EFFORT",
    6: "APPROVAL_REWORK_EXCEPTIONS",
  }), []);

  const payloadGroups = useMemo(() => 
    selectedSections
      .map((id) => sectionToGroupMap[id])
      .filter(Boolean),
    [selectedSections, sectionToGroupMap]
  );

  const { refetch: triggerReport, isFetching: isGeneratingReport } = useGetGeneratedReport({
    remote_utility_id: remoteUtilityId || "",
    start_date: dateParams?.startDate,
    end_date: dateParams?.endDate,
    groups: payloadGroups,
  });

  const handleGenerateReport = () => {
    if (payloadGroups.length === 0 || !remoteUtilityId) {
      return;
    }

    triggerReport();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] p-6 max-h-[85vh] overflow-y-auto custom-scrollbar">
        <DialogHeader>
          <DialogTitle className="font-semibold">
            Generate Service Order Report
          </DialogTitle>
          <p className="text-gray-500">
            Select the date range and data sections for your report.
          </p>
        </DialogHeader>
        
        <div className="py-2 space-y-4 mt-2">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <DynamicForm
                form={exportForm}
                fields={exportFields}
                gridColumns={2}
                config={{ classes: FIELD_CLASSES }}
                renderCustomFooter={() => <div className="hidden" />}
              />
              {dateParams && countsData && countsData?.total !== undefined && (
                <div className="text-[14px] font-semibold text-gray-800 mt-2 ml-1">
                  Found: {countsData.total} Orders
                </div>
              )}
            </div>
            <div className="pt-[28px]">
              <Button
                onClick={exportForm.handleSubmit((data) => {
                  setDateParams({
                    startDate: data.startDate,
                    endDate: data.endDate,
                  });
                  setTimeout(() => refetchCounts(), 0);
                })}
                disabled={isFetchingCounts}
              >
                {isFetchingCounts ? "Applying..." : "Apply"}
              </Button>
            </div>
          </div>
          
          <div className="bg-blue-100 border border-blue-50 rounded-lg p-2.5 flex gap-2 text-sm text-gray-700">
            <Info className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
            <p>
              The export file will be sent to your registered email ID. This
              process may take some time depending on the dataset size.
            </p>
          </div>
          
          <div className="border-t border-gray-100 pt-5 mt-6 flex justify-between items-center mb-3">
            <span className="text-sm font-semibold text-gray-700">
              {selectedSections.length} of {exportSections.length} sections
              selected
            </span>
            <Button
              type="button"
              onClick={handleSelectAll}
              variant="ghost"
            >
              {selectedSections.length === exportSections.length
                ? "Deselect All"
                : "Select All"}
            </Button>
          </div>
          
          <div className="max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
            <Accordion type="multiple" className="w-full space-y-3">
              {exportSections.map((section) => {
                const isSelected = selectedSections.includes(section.id);
                return (
                  <AccordionItem
                    key={section.id}
                    value={`section-${section.id}`}
                    className={cn(
                      "border rounded-lg bg-white overflow-hidden shadow-sm",
                      isSelected ? "border-gray-300" : "border-gray-200"
                    )}
                  >
                    <div className="flex items-start w-full pt-4">
                      <div className="pt-0.5 mr-3 pl-4">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() =>
                            handleToggleSection(section.id)
                          }
                          className={cn(
                            "w-5 h-5 rounded-[4px] shadow-none transition-all",
               
                          )}
                        />
                      </div>
                      <AccordionTrigger
                        className={cn(
                          "hover:no-underline py-0 pb-4 flex-1 w-full relative flex items-start justify-between pr-4",
                       
                        )}
                      >
                        <div className="flex flex-col items-start justify-start flex-1 mr-4">
                          <span className=" font-semibold text-gray-900 leading-none mb-1.5 flex flex-col text-left items-start w-full">
                            {section.title}
                          </span>
                          <span className=" font-normal text-gray-500 leading-snug flex flex-col text-left items-start w-full">
                            {section.description}
                          </span>
                        </div>
                      </AccordionTrigger>
                    </div>
                    <AccordionContent className="px-12 pb-4 pt-1">
                      <div className="flex flex-wrap gap-2">
                        {section.fields.map((field) => (
                          <span key={field} className="px-3 py-1.5 bg-[#F9FAFB] border border-gray-200 text-gray-700 rounded-md text-[13px] font-medium leading-none">
                            {field}
                          </span>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
          
          <div className="border-t border-gray-100 pt-6 flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateReport}
              disabled={isGeneratingReport || selectedSections.length === 0}
            >
              <Download className="w-4 h-4" /> {isGeneratingReport ? "Downloading..." : "Download Report"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
